import { memo, useEffect } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

import type {
	HelpCenterArticle,
	HelpCenterGroup,
	HelpCenterGroupTranslation,
} from "@productlane/zero"
import { Button, Dialog, Input } from "@productlane/ui"

import type { Language } from "@/screens/settings/settings-pages/language-selector"
import { analytics } from "@/lib/analytics"
import { useHelpCenterGroupTranslations } from "@/lib/use-item-translations"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { renameGroup } from "./rename-group"

const addGroupSchema = z.object({
	groupName: z.string().min(1, "Group name is required"),
})

type RenameGroupFormData = z.infer<typeof addGroupSchema>

type HelpCenterGroupWithTranslations = HelpCenterGroup & {
	helpCenterArticles: readonly HelpCenterArticle[]
	translations?: readonly HelpCenterGroupTranslation[]
}

export const RenameGroupDialog = memo(
	({
		group,
		selectedLanguage,
		isOpen,
		onClose,
	}: {
		group: HelpCenterGroupWithTranslations
		selectedLanguage: Language
		isOpen: boolean
		onClose: () => void
	}) => {
		const zero = useZero()
		const workspace = useZeroWorkspace()
		const { selectedTranslation, defaultLanguage } =
			useHelpCenterGroupTranslations(group, selectedLanguage)

		const form = useForm<RenameGroupFormData>({
			resolver: zodResolver(addGroupSchema),
			defaultValues: {
				groupName: selectedTranslation.title || group.name,
			},
		})

		const onSubmit = form.handleSubmit(async (data) => {
			if (!workspace) return

			await renameGroup({
				zero,
				workspaceId: workspace.id,
				defaultLanguage,
				group,
				language: selectedLanguage.code,
				name: data.groupName,
			})

			analytics.track("rename-helpcenter-group", {
				groupName: data.groupName,
				language: selectedLanguage.code,
				popover: true,
			})
			form.reset()
			onClose()
		})

		const onCancel = () => {
			form.reset()
			onClose()
		}

		useEffect(() => {
			form.reset({ groupName: selectedTranslation.title || group.name })
		}, [form, group.name, selectedTranslation.title, selectedLanguage.code])

		return (
			<Dialog
				className="max-w-80 p-2"
				isOpen={isOpen}
				onOpenChange={(state) => {
					if (state === false) onCancel()
				}}
			>
				<h2 className="mx-2 mb-4 mt-1 text-lg font-bold">Rename group</h2>
				<form onSubmit={onSubmit} className="space-y-2 px-2 py-2">
					<Input
						{...form.register("groupName")}
						type="text"
						placeholder="Group name"
						className="w-full text-sm text-primary outline-none"
					/>
					{form.formState.errors.groupName && (
						<p className="text-xs text-red-500">
							{form.formState.errors.groupName.message}
						</p>
					)}
					<Button
						type="submit"
						variant="outline"
						className="w-full rounded-md border p-2"
					>
						Rename group
					</Button>
				</form>
			</Dialog>
		)
	},
)
